// Remember all the "Save Steps" that the user stored, mixed in with the

import { defineStore } from "pinia";
import { AnyParams, pack, ToolParams } from "./AllParams";
import { useCurrentUser } from "./CommonGlobals";
import { EXPORT_KIND } from "./Export";
import { Operation } from "./Operation";
import { PERSISTENT_USER } from "./PersistentUser";
import { mergeToolbox } from "./Sync";
import { isOp, keyForTool, MultistepTool, OpTool, Tool, ToolKind
    } from "./ToolKind";
import { Units } from "./Units";

/// An interface for window.localStorage so we can swap in a mock during tests.
export type WindowLocalStorage = Pick<Storage, 'getItem' | 'setItem'>;

/// Maps the name of the Multistep tool that was removed from toolbox
/// to last time the tool was removed.
export type Tombstones = Record<string, string>;

// The toolbox as it's stored in local storage.
export interface PackedToolbox {
    /// The schema version for PackedToolbox.
    v: 1 | 2,
    tools: Tool[],
    /// When was the *order* of the tools last changed?
    orderTimestamp?: string
    // New in version 2
    /// Maps the name of the Multistep tool that was removed from toolbox
    /// to last time the tool was removed.
    tombstones?: Tombstones
}
// Increment this number whenever PackedToolbox changes.
const PACKED_TOOLBOX_SCHEMA_VERSION = 2;


/// The user should always see the full set of operations, so restore them
/// if they somehow got deleted.  More likely, the user upgraded to a new
/// version of PDF Snake with a new operation kind.
function addMissingOperations(packedTools: Tool[]): void {
    const seenOps: Operation[] = [];
    for (const tool of packedTools) {
        if (tool.kind == ToolKind.Op) {
            seenOps.push(tool.op);
        }
    }
    for (const op of Object.values(Operation)) {
        if (!seenOps.includes(op)) {
            packedTools.push({
                kind: ToolKind.Op,
                op: op,
            });
        }
    }
}

export const EPOCH = "1970-01-01T00:00:00.000Z";

export function emptyPackedToolbox(): PackedToolbox {
    return {
        v: PACKED_TOOLBOX_SCHEMA_VERSION,
        tools: []
    };
}

/// Reorder the toolbox according to tools, and add new tools to toolbox.
/// Remove tools from toolbox that are listed in trash.
// exported for testing only.
export function updateToolbox(packedToolbox: PackedToolbox, tools: Tool[],
    trash: Tombstones, now: () => string): void 
{
    const result = mergeToolbox(packedToolbox.tools, packedToolbox.tombstones ?? {},
        tools, trash, {
            getKey: keyForTool,
            getTimestamp: tool => tool.modTimestamp ?? EPOCH,
            getEpitaph: tool => isOp(tool) ? null : tool.name
        });   

    // Update the toolbox.
    packedToolbox.v = PACKED_TOOLBOX_SCHEMA_VERSION;
    packedToolbox.tools = result.tools;
    packedToolbox.tombstones = result.tombstones;
    if (result.orderChanged) {
        packedToolbox.orderTimestamp = now();
    }
}


export class Toolbox {
    // Every method rereads the list of tools from local storage before writing
    // them back to local storage.  In other words, never keep the list of tools
    // in memory, because the user may have multiple windows open.  They'd be
    // surprised and unhappy if they saved a new MultistepTool in one window,
    // then closed another window and the new MultistepTool disappeared.
    private readonly localStorage: WindowLocalStorage;
    private readonly now: () => string;
    private readonly storageKey: string;

    constructor(localStorage: WindowLocalStorage,
        now: () => string, uid?: string | null)
    {
        if (undefined === uid) {
            uid = window.localStorage.getItem(PERSISTENT_USER);
        }
        this.localStorage = localStorage;
        this.now = now;
        this.storageKey = uid ? `TOOLBOX:${uid}` : 'TOOLBOX';
    }

    isEmpty(): boolean {
        return !this.localStorage.getItem(this.storageKey);
    }

    /// Remember all the settings for these Operations so the user sees the same
    /// settings the next time they use the Operation.
    storeOps(ops: OpTool[]) {
        const opMap = new Map(ops.map(op => [op.op, op]));
        const packedToolbox = this.loadToolsFromLocalStorage();
        const now = this.now();
        for (const tool of packedToolbox.tools) {
            if (isOp(tool)) {
                const packedParams = opMap.get(tool.op)?.packedParams;
                if (packedParams) {
                    tool.packedParams = packedParams;
                    tool.modTimestamp = now;
                    opMap.delete(tool.op);
                }
            }
        }
        packedToolbox.tools.push(...opMap.values());
        this.storeToolsInLocalStorage(packedToolbox);
    }

    /// Store a multistep tool when the user clicks "Record Steps".
    storeMultistep(freshTool: MultistepTool) {
        freshTool.modTimestamp = this.now();
        freshTool.pushed = false;
        const packedToolbox = this.loadToolsFromLocalStorage();
        // Update the tool if it already exists.
        let exists = false;
        for (const tool of packedToolbox.tools) {
            if (tool.kind == ToolKind.Multistep
                && tool.name == freshTool.name)
            {
                Object.assign(tool, freshTool);
                exists = true;
                break;
            }
        }
        if (!exists) {
            // Append a new tool.
            packedToolbox.tools.push(freshTool);
        }
        // Remove any matching tombstone.
        if (packedToolbox.tombstones) {
            delete packedToolbox.tombstones[freshTool.name];
        }
        this.storeToolsInLocalStorage(packedToolbox);
    }

    /// Load the list of tools for display in the What's next? box.
    loadTools(): Tool[] {
        return this.loadToolsFromLocalStorage().tools;
    }

    /// Store the tools after the user may have re-arranged them or deleted
    /// some via the What's next? box.
    storeWhatsNext(tools: Tool[], trash: MultistepTool[]): void {
        const packedToolbox = this.loadToolsFromLocalStorage();
        const tombstones: Tombstones = {};
        for (const trashed_tool of trash) {
            tombstones[trashed_tool.name] = this.now();
        }
        updateToolbox(packedToolbox, tools, tombstones, this.now);
        this.storeToolsInLocalStorage(packedToolbox);
    }

    loadPackedToolsFromLocalStorage(): PackedToolbox | undefined {
        const jsonText = this.localStorage.getItem(this.storageKey);
        return jsonText ? (JSON.parse(jsonText) as PackedToolbox) : undefined;
    }

    private loadToolsFromLocalStorage(): PackedToolbox {
        const toolbox: PackedToolbox = this.loadPackedToolsFromLocalStorage() ??
            { v: PACKED_TOOLBOX_SCHEMA_VERSION, tools: [] };
        addMissingOperations(toolbox.tools);
        return toolbox;
    }

    storeToolsInLocalStorage(toolbox: PackedToolbox) {
        this.localStorage.setItem(this.storageKey, JSON.stringify(toolbox));
    }

    /// Imports tools into local storage.  Avoids name collisions by appending
    /// numbers to colliding names.
    /// Returns the list of new tools that were imported.
    importTools(newTools: MultistepTool[]): MultistepTool[] {
        const packedToolbox = this.loadToolsFromLocalStorage();
        const oldToolNames: string[] = [];
        for (const oldTool of packedToolbox.tools) {
            if (oldTool.kind == ToolKind.Multistep) {
                oldToolNames.push(oldTool.name);
            }
        }
        // Iterate through all the tools looking for name collisions,
        // and rename them when there is a collision.
        const renamedNewTools: MultistepTool[] = newTools.map(newTool => {
            if (oldToolNames.includes(newTool.name)) {
                // Collision.  Craft a unique name.
                let newName = `${newTool.name}(imported)`;
                let n = 1;
                while (oldToolNames.includes(newName)) {
                    n += 1;
                    newName = `${newTool.name}(imported ${n})`;
                }
                oldToolNames.push(newName);
                return {
                    ...newTool,
                    name: newName
                };
            } else {
                return newTool;
            }
        });
        packedToolbox.tools.push(...renamedNewTools);
        this.storeToolsInLocalStorage(packedToolbox);
        return renamedNewTools;
    }

    // TODO: remove this function in January 2025.
    /// Only call when this toolbox isEmpty().
    /// Import tools from the old schema into the new toolbox schema.
    immigrateTools(tools: Tool[]) {
        this.storeToolsInLocalStorage({ 
            v: PACKED_TOOLBOX_SCHEMA_VERSION,
            tools
        });
    }
}

export async function deserializeExportedSteps(
    f: File): Promise<MultistepTool[]>
{
    // Unpack the saved steps .json that was dropped by the user.
    const buffer = await f.arrayBuffer();
    const text = new TextDecoder("utf-8").decode(buffer);
    const json = JSON.parse(text);
    if (json?.kind !== EXPORT_KIND) {
        throw new Error("File doesn't contain saved steps.");
    }
    return json.xtools as MultistepTool[];
}

export function serializeToolsForExport(xtools: MultistepTool[]): string {
    return JSON.stringify({ 'kind': EXPORT_KIND, xtools});   
}

export const nowJSON = () => new Date().toJSON();

// Why we can't keep a copy of the toolbox in memory:
// Imagine the user has two browser windows open.  In one window, the user
// saves a new multi step tool to the tool box.  Then they close the window.
// In the other window, the user reorders the toolbox by dragging one tool
// into a new position.  If each browser window had a copy of the toolbox
// in memory, the new multistep tool from the first window would be lost.

/// Returns a toolbox for the current user.  Don't keep for long because
/// the user may sign in or sign out.
/// Pass null to get the unsigned-in anonymous toolbox.
export function getToolbox(uid?: string | null): Toolbox {
    return new Toolbox(window.localStorage, nowJSON,
        uid === undefined ? useCurrentUser().id : uid);
}

export function storeParams(paramsList: ToolParams[], units: Units): void {
    const opTools = paramsList.map(params => {
        const tool: OpTool = {
            op: params.kind as Operation,
            kind: ToolKind.Op,
            packedParams: pack(params, units)
        };
        return tool;
    });
    getToolbox().storeOps(opTools);
}

export const useToolboxUser = defineStore('toolboxUser', {
    state: () => {
        return {
            firebaseUid: undefined as string | undefined,
        }
    },
});


/// Stores one saved steps to local storage.
export function storeSteps(name: string, paramsList: AnyParams[], units: Units,
    thumbnailUrl: string, thumbnailWidth: number, thumbnailHeight: number): void
{
    const toolbox = getToolbox();
    const tool: MultistepTool = {
        kind: ToolKind.Multistep,
        name,
        thumbnailHeight,
        thumbnailUrl,
        thumbnailWidth,
        packedSteps: paramsList.map(params => pack(params, units))
    };
    toolbox.storeMultistep(tool);
}
